/**
 * L7 Platform — progressive accordion enhancement for the homepage FAQ band.
 *
 * Transforms `.l7-faq-section .l7-feature-list` (and `.l7-faq` opt-ins) into a
 * single-open accordion: each item's title becomes a `<button>` with
 * `aria-expanded` / `aria-controls`, and the body panel expands below the
 * question. Without JS the list renders exactly as authored — no content
 * loss. Never mutates the Visual Builder DOM (`body.et-fb`).
 *
 * Selector is intentionally distinct from `tabs.js` (`.l7-tabs` /
 * `.l7-why-section`) so Why L7|ESP tabs are unaffected.
 */
(function (window, document) {
	'use strict';

	var SELECTOR =
		'.l7-faq-section .l7-feature-list, .l7-faq .l7-feature-list, .l7-feature-list.l7-faq';

	/**
	 * @return {boolean}
	 */
	function isVisualBuilder() {
		return document.body && document.body.classList.contains('et-fb');
	}

	/**
	 * Unique id fragment for aria wiring.
	 *
	 * @param {number} index List index on the page.
	 * @return {string}
	 */
	function uid(index) {
		return 'l7-faq-' + index;
	}

	/**
	 * Collect Divi feature child wrappers for a list root.
	 *
	 * @param {Element} list `.l7-feature-list` root.
	 * @return {Element[]}
	 */
	function collectItems(list) {
		var itemsRoot = list.querySelector('.l7-feature-list__items');
		if (!itemsRoot) {
			return [];
		}

		var wrappers = itemsRoot.querySelectorAll(
			':scope > .et_pb_module.l7_feature, :scope > .l7_feature, :scope > .l7-feature-list__item'
		);

		if (wrappers.length) {
			return Array.prototype.slice.call(wrappers);
		}

		return Array.prototype.slice.call(
			itemsRoot.querySelectorAll('.l7-feature-list__item')
		);
	}

	/**
	 * Resolve the inner `.l7-feature-list__item` (or the node itself).
	 *
	 * @param {Element} wrapper Child wrapper or item.
	 * @return {Element|null}
	 */
	function resolveItem(wrapper) {
		if (wrapper.classList.contains('l7-feature-list__item')) {
			return wrapper;
		}
		return wrapper.querySelector('.l7-feature-list__item');
	}

	/**
	 * Accent the trailing "7 questions." in the FAQ section heading.
	 * Runs independently of accordion enhancement (items may be empty).
	 * Copy-coupled: only matches a literal "7 questions." / "7 questions" suffix.
	 *
	 * @param {Element} section `.l7-faq-section` root.
	 * @return {void}
	 */
	function accentHeading(section) {
		if (!section) {
			return;
		}

		var heading = section.querySelector('.l7-section-header__heading');
		if (!heading || heading.querySelector('.l7-faq__heading-accent')) {
			return;
		}

		var text = heading.textContent.replace(/\s+/g, ' ').trim();
		var match = text.match(/^(.*?)(7 questions\.?)$/i);
		if (!match) {
			return;
		}

		heading.textContent = '';
		if (match[1]) {
			heading.appendChild(document.createTextNode(match[1]));
		}

		var accent = document.createElement('span');
		accent.className = 'l7-faq__heading-accent';
		accent.textContent = match[2];
		heading.appendChild(accent);
	}

	/**
	 * Open/close one item; optionally close siblings (single-open).
	 *
	 * @param {Element[]} items Item roots.
	 * @param {Element[]} buttons Toggle buttons.
	 * @param {Element[]} panels Collapsible panels.
	 * @param {number} index Target index.
	 * @param {boolean} open Whether to open.
	 * @return {void}
	 */
	function setOpen(items, buttons, panels, index, open) {
		var i;
		for (i = 0; i < items.length; i++) {
			var isTarget = i === index;
			var shouldOpen = isTarget ? open : false;

			buttons[i].setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
			items[i].classList.toggle('is-open', shouldOpen);

			if (shouldOpen) {
				panels[i].removeAttribute('hidden');
			} else {
				panels[i].setAttribute('hidden', '');
			}

			var indicator = buttons[i].querySelector('.l7-faq__indicator');
			if (indicator) {
				indicator.textContent = shouldOpen ? '\u2212' : '+';
			}
		}
	}

	/**
	 * Enhance a single opted-in feature list into an accordion.
	 *
	 * @param {Element} list `.l7-feature-list` root.
	 * @param {number} listIndex Index among enhanced lists.
	 * @return {void}
	 */
	function enhanceList(list, listIndex) {
		if (list.getAttribute('data-l7-faq') === '1') {
			return;
		}

		var wrappers = collectItems(list);
		if (!wrappers.length) {
			return;
		}

		var base = uid(listIndex);
		var items = [];
		var buttons = [];
		var panels = [];
		var i;

		for (i = 0; i < wrappers.length; i++) {
			var item = resolveItem(wrappers[i]);
			if (!item) {
				continue;
			}

			var title = item.querySelector('.l7-feature-list__title');
			var body = item.querySelector('.l7-feature-list__body');
			if (!title || !body) {
				continue;
			}

			var panelId = base + '-panel-' + i;
			var label = title.textContent.replace(/\s+/g, ' ').trim() || 'Item ' + (i + 1);

			var button = document.createElement('button');
			button.type = 'button';
			button.className = 'l7-faq__trigger';
			button.setAttribute('aria-expanded', 'false');
			button.setAttribute('aria-controls', panelId);

			var question = document.createElement('span');
			question.className = 'l7-faq__question';
			question.textContent = label;

			var indicator = document.createElement('span');
			indicator.className = 'l7-faq__indicator';
			indicator.setAttribute('aria-hidden', 'true');
			indicator.textContent = '+';

			button.appendChild(question);
			button.appendChild(indicator);

			title.textContent = '';
			title.appendChild(button);

			body.id = panelId;
			body.setAttribute('hidden', '');
			body.classList.add('l7-faq__panel');

			items.push(item);
			buttons.push(button);
			panels.push(body);
		}

		if (!items.length) {
			return;
		}

		list.classList.add('l7-faq--enhanced');
		list.setAttribute('data-l7-faq', '1');

		// Comp default: first item open, single-open accordion.
		setOpen(items, buttons, panels, 0, true);

		list.addEventListener('click', function (event) {
			var target = event.target;
			if (!target || !target.closest) {
				return;
			}
			var button = target.closest('.l7-faq__trigger');
			if (!button || !list.contains(button)) {
				return;
			}

			var index = buttons.indexOf(button);
			if (index < 0) {
				return;
			}

			event.preventDefault();
			var isOpen = button.getAttribute('aria-expanded') === 'true';
			setOpen(items, buttons, panels, index, !isOpen);
		});
	}

	/**
	 * @return {void}
	 */
	function init() {
		if (isVisualBuilder()) {
			return;
		}

		// Heading accent is independent of accordion item enhancement.
		var sections = document.querySelectorAll('.l7-faq-section');
		var s;
		for (s = 0; s < sections.length; s++) {
			accentHeading(sections[s]);
		}

		var lists = document.querySelectorAll(SELECTOR);
		var seen = [];
		var i;

		for (i = 0; i < lists.length; i++) {
			var list = lists[i];
			if (seen.indexOf(list) !== -1) {
				continue;
			}
			seen.push(list);
			enhanceList(list, seen.length - 1);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})(window, document);
